import { Impact } from "../generated/proto-ts/main"
import { IKinematicPoint } from "../types"
import { meanArr } from "./common"
import {
  IImpactKinematics,
  G,
  FREE_FALL_ACCELERATION_THRESHOLD,
  SIGNAL_FILTER_SLIDING_WINDOW_SIZE_MS,
} from "./types"

export const getSignedQuadraticSum = (
  ax_points: number[],
  ay_points: number[],
  az_points: number[],
): number[] => {
  let aq_points: number[] = []
  for (let i = 0; i < ax_points.length; i++) {
    let values: number[] = [ax_points[i]!] // ax point is guaranteed, ay and az point might be undefined
    let ay = ay_points[i]
    if (ay !== undefined) {
      values.push(ay)
    }
    let az = az_points[i]
    if (az !== undefined) {
      values.push(az)
    }
    let mean = meanArr(values)
    let quadraticSum: number = 0
    for (let j = 0; j < values.length; j++) {
      quadraticSum += Math.pow(values[j]!, 2)
    }
    quadraticSum = Math.sqrt(quadraticSum)
    if (mean < 0) {
      quadraticSum = -quadraticSum
    }
    aq_points.push(quadraticSum)
  }
  return aq_points
}

const applySlidingMeanFilter = (data: number[], windowSize: number): number[] => {
  if (windowSize <= 1) {
    return data
  }
  if (data.length < windowSize) {
    windowSize = data.length
  }
  const halfWindowSize = Math.floor(windowSize / 2)
  const smoothedData: number[] = new Array(data.length).fill(0)
  for (let i = 0; i < data.length; i++) {
    let sum = 0
    let count = 0
    for (let j = -halfWindowSize; j <= halfWindowSize; j++) {
      if (i + j >= 0 && i + j < data.length) {
        sum += data[i + j]!
        count++
      } else {
        sum += data[i]!
        count++
      }
    }
    smoothedData[i] = sum / count
  }
  return smoothedData
}

export const getImpactKinematics = (
  impact: Impact,
  aq_points: number[],
): IImpactKinematics | null => {
  let f = impact.decimated_frequency
  if (f === 0) {
    f = impact.sampling_frequency
  }
  if (f === 0) {
    console.warn(`getImpactKinematics: f=0 Hz, using 1kHz as default`)
    f = 1e3
  }
  //   console.debug(`getImpactKinematics: f=${f} Hz`)

  // UPD: 2024-06-01 frequency is now transmitted in the impact message
  // let nFF = impact.impact_index // n
  // let fEstimate = nFF / tFF // Hz (1/s)
  // let fCorrected = Math.round(fEstimate / 1e3) * 1e3 // 1kHZ step, e.g. 5kHz, 10kHz, 20kHz etc...

  // UPD 2026-06-25 Apply a sliding mean filter to aq_points and use the filtered points
  // anywhere a comparison to the FREE_FALL_ACCELERATION_THRESHOLD is required
  let windowSizeMs = SIGNAL_FILTER_SLIDING_WINDOW_SIZE_MS // ms
  let windowSize = Math.round(f / (1e3 / windowSizeMs)) // samples
  let aq_points_filtered = applySlidingMeanFilter(aq_points, windowSize)
  let aa_points_filtered = aq_points_filtered.map((q) => G * q) // m/s^2

  // UPD 2026-06-25 TFF and FFH are corrected by offsetting the TFF backwards
  // to the point where the acceleration curve crosses the FREE_FALL_ACCELERATION_THRESHOLD (and not 0g as before)
  let index_offset = 0
  for (let i = impact.impact_table_impact_index; i >= 0; i--) {
    let a = aa_points_filtered[i]
    if (a === undefined || a < FREE_FALL_ACCELERATION_THRESHOLD) {
      break
    }
    index_offset++
  }
  let impact_sample_impact_index_corrected = impact.impact_sample_impact_index - index_offset
  console.log(
    `getImpactKinematics: initial impact_index=${impact.impact_sample_impact_index}, index_offset=${index_offset}, impact_index_corrected=${impact_sample_impact_index_corrected}`,
  )

  // UPD 2024-06-26 tFF is now calculated based on the impact index (impact_tstart_sample_index)
  let calculatedTFF = impact_sample_impact_index_corrected / f // s
  let calculatedFFH = (G * Math.pow(calculatedTFF, 2)) / 2 // m

  let tFF_T0 = (impact_sample_impact_index_corrected - impact.impact_table_impact_index) / f
  let vInit = G * tFF_T0 // m/s

  //   console.log(
  //     `getImpactKinematicsSM: impactHeight(.impact_ffh)=${impact.impact_ffh}m, calculatedFFH=${calculatedFFH}m, , calculatedTFF=${calculatedTFF}s, tFF@T0=${tFF_T0}, vInit=${vInit}m/s`,
  //   )

  let vCurr = vInit
  let tCurr = 0

  let vMax = 0
  let vMin = 0
  let gMax = 0
  let vAtGmax = 0
  let iGmax = 0
  let kinematicPoints: IKinematicPoint[] = []

  // First integral: velocity
  for (let i = 0; i < aq_points.length; i++) {
    let currQ = aq_points[i]!
    let nextQ = aq_points[i + 1]
    if (nextQ === undefined) {
      break
    }
    // point index (sequence) and its delta
    // let dN = i + 1 - i
    // let nCurr = (i + i + 1) / 2 // n; i.e. trapezoidal rule

    // time and its delta
    let dT = 1 / f // s
    tCurr = i / f // s

    // mean acceleration (trapezoidal rule)
    let qMean = (currQ + nextQ) / 2 // g
    let aCurr = G * qMean // m/s^2

    // integrated velocity
    let dV = dT * aCurr // m/s
    vCurr -= dV
    vMin = Math.min(vCurr, vMin)
    vMax = Math.max(vCurr, vMax)
    if (qMean > gMax) {
      gMax = qMean
      iGmax = i
      vAtGmax = vCurr
    }

    kinematicPoints.push({ g: qMean, a: aCurr, v: vCurr, t: tCurr, d: NaN })
  }

  let dCurr = 0
  // Second integral: displacement
  for (let i = 0; i < kinematicPoints.length; i++) {
    let currKinematicPoint = kinematicPoints[i]!
    let nextKinematicPoint = kinematicPoints[i + 1]
    if (nextKinematicPoint === undefined) {
      break
    }
    let tCurr = currKinematicPoint.t
    let tNext = nextKinematicPoint.t

    let vCurr = currKinematicPoint.v
    let vNext = nextKinematicPoint.v

    let dT = tNext - tCurr // s
    let vMean = (vCurr + vNext) / 2 // m/s (trapezoidal rule)

    let dD = dT * vMean // m
    dCurr -= dD
    nextKinematicPoint.d = dCurr
  }

  //   console.log(
  //     `getImpactKinematicsSM: vMax=${vMax} m/s, vMin=${vMin} m/s, gMax=${gMax} g, vAtGmax=${vAtGmax} m/s, iGmax=${iGmax}, tAtGmax=${kinematicPoints[iGmax]!.t} s`,
  //   )

  // Calculate INIT and FINAL values
  let iInit = 0
  let tInit = 0
  let dInit = 0
  // let vInit = 0
  // Search for iInit starting from iGmax (backward)
  for (let i = iGmax; i >= 0; i--) {
    let kp = kinematicPoints[i]!
    let _a = aa_points_filtered[i]
    let a = _a !== undefined ? _a : kp.a
    if (a < FREE_FALL_ACCELERATION_THRESHOLD) {
      break
    }
    iInit = i + 1 // i+1 beacuse there is a shift when integrating
    tInit = kp.t
    vInit = kp.v
    dInit = kp.d
  }

  // Offset distance
  for (let i = 0; i < kinematicPoints.length; i++) {
    let kp = kinematicPoints[i]!
    kp.d -= dInit
  }
  dInit -= dInit

  console.log(`getImpactKinematicsSM: vInit=${vInit} m/s, tInit=${tInit}, dInit=${dInit} m`)

  let iFinal = 0
  let tFinal = 0
  let vFinal = 0
  let dFinal = 0
  // Search for iFinal starting from  iGmax (forward)
  for (let i = iGmax; i < kinematicPoints.length; i++) {
    let kp = kinematicPoints[i]!
    let _a = aa_points_filtered[i]
    let a = _a !== undefined ? _a : kp.a
    if (a < FREE_FALL_ACCELERATION_THRESHOLD) {
      break
    }
    iFinal = i
    tFinal = kp.t
    vFinal = kp.v
    dFinal = kp.d
  }
  console.log(
    `getImpactKinematicsSM: vFinal=${vFinal} m/s, tFinal=${tFinal}, dFinal=${dFinal} m, iFinal=${iFinal}`,
  )

  let iSecondarySuspense = 0
  let tSecondarySuspense = 0
  let dSecondarySuspense = 0
  for (let i = iFinal; i < kinematicPoints.length - 1; i++) {
    if (i === 0) {
      continue
    }
    let _vPrev = kinematicPoints[i - 1]!.v
    let _vCurr = kinematicPoints[i]!.v
    // negative velocity here is going up, positive - down
    if (_vPrev < 0 && _vCurr > 0) {
      break
    }
    let kp = kinematicPoints[i + 1]! // i+1 because there is a shift when integrating
    tSecondarySuspense = kp.t
    dSecondarySuspense = kp.d
    iSecondarySuspense = i + 1
  }
  let dSecondarySuspenseFromFinal = dSecondarySuspense - dFinal
  console.log(
    `getImpactKinematicsSM: tSecondarySuspense=${tSecondarySuspense}, dSecondarySuspense=${dSecondarySuspense} m, dSecondarySuspenseFromFinal=${dSecondarySuspenseFromFinal} m`,
  )

  // Search for the dMin value somewhere between iInit and iFinal
  let dMaxDeflection = 0
  let tMaxDeflection = 0
  for (let i = iInit; i < iFinal; i++) {
    let kp = kinematicPoints[i]!
    if (kp.d < dMaxDeflection) {
      dMaxDeflection = kp.d
      tMaxDeflection = kp.t
    }
  }
  // console.log(`getImpactKinematicsSM: dMin=${dMin} m, tAtDMin=${tAtDMin} s`)

  let impactDurationS = tFinal - tInit // s
  let calculatedDurationMS = 1e3 * impactDurationS // ms

  let resilience = Math.pow(vFinal, 2) / Math.pow(vInit, 2) // ratio
  console.log(
    `getImpactKinematicsSM: vFinal=${vFinal} m/s, tFinal=${tFinal}, dFinal=${dFinal} m, R=${resilience}, dMaxDeflection=${dMaxDeflection} m`,
  )
  let deflectionDistanceMM = Math.abs(dMaxDeflection * 1e3) // mm
  let resiliencePerc = 100 * resilience // %
  return {
    calculatedDurationMS,
    calculatedTFF,
    calculatedFFH,
    calculatedHIC: {
      hicMax: 0,
      hicTms: 0,
      hicDTms: 0,
    },
    calculatedGmax: gMax,

    samplingFrequency: f,

    initialVelocity: vInit,
    finalVelocity: vFinal,
    deflectionDistanceMM,
    deflectionTimeMS: tMaxDeflection * 1e3,
    resiliencePerc,

    secondarySuspenseHeightM: dSecondarySuspense,
    secondarySuspenseTimeMS: 1e3 * tSecondarySuspense,

    kinematicPoints,
  }
}
